import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { blue, green, amber, grey, blueGrey, deepOrange } from "@mui/material/colors";
import { FS } from "@plannel/utils/font-util";

/**
 * FLO 차트 범례(기호 설명). 도움말('?') 아이콘 호버 시 표시.
 * 스와치는 실제 노드/박스 스타일(FloNodes.js / FloChart.scss)을 축소해 그린다.
 */
const Row = ({ swatch, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.4 }}>
    <Box sx={{ width: 26, display: "flex", justifyContent: "center", flexShrink: 0 }}>{swatch}</Box>
    <Typography variant="caption" sx={{ color: grey[800], lineHeight: 1.3 }}>
      {label}
    </Typography>
  </Box>
);

const FloLegend = () => {
  const { t } = useTranslation();

  return (
    <Box sx={{ p: 0.5, minWidth: 250 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {t("floLegendTitle")}
      </Typography>

      {/* 품목 — 삼각형 */}
      <Row
        label={t("floKindItem")}
        swatch={<Box sx={{ width: 16, height: 14, clipPath: "polygon(50% 0%, 100% 100%, 0% 100%)", bgcolor: green[300] }} />}
      />
      {/* 대체 자재 — 해치 채움 삼각형 + 점선 (품목과 같은 모양, 채움으로 구분) */}
      <Row
        label={t("floLegendAltSupply")}
        swatch={
          <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
            <Box
              sx={{
                width: 16,
                height: 14,
                flexShrink: 0,
                clipPath: "polygon(50% 0%, 100% 100%, 0% 100%)",
                backgroundImage: `repeating-linear-gradient(45deg, ${green[100]} 0 3px, ${green[400]} 3px 6px)`,
              }}
            />
            <Box sx={{ width: 10, borderTop: `1.5px dashed ${grey[600]}`, flexShrink: 0 }} />
          </Box>
        }
      />
      {/* 라우트 — 둥근 사각형 */}
      <Row
        label={t("floKindRoute")}
        swatch={<Box sx={{ width: 22, height: 14, borderRadius: "4px", border: `2px solid ${blue[600]}`, bgcolor: blue[100] }} />}
      />
      {/* 자원 — 타원 */}
      <Row
        label={t("floKindResource")}
        swatch={<Box sx={{ width: 22, height: 14, borderRadius: "50%", border: `2px solid ${amber[700]}`, bgcolor: amber[100] }} />}
      />
      {/* 결합(AND) — 실선 박스 */}
      <Row
        label={t("floLegendCombine")}
        swatch={<Box sx={{ width: 22, height: 16, borderRadius: "4px", border: `1.5px solid ${green[800]}`, bgcolor: "rgba(46,125,50,0.06)" }} />}
      />
      {/* 택1(OR) — ○ 정션(라우트) / 점선 박스(자원) */}
      <Row
        label={t("floLegendAlternate")}
        swatch={
          <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
            <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: grey[600], flexShrink: 0 }} />
            <Box sx={{ width: 14, height: 13, borderRadius: "3px", border: `1.5px dashed ${blueGrey[400]}`, bgcolor: "rgba(96,125,139,0.05)", flexShrink: 0 }} />
          </Box>
        }
      />
      {/* BOM 소비 비율 — 녹색 배지(×n) */}
      <Row
        label={t("floLegendRatio")}
        swatch={
          <Box
            sx={{
              minWidth: 18,
              height: 14,
              px: "3px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "7px",
              border: `1px solid ${green[600]}`,
              bgcolor: green[100],
              color: "#000",
              fontSize: FS.CAPTION,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            ×n
          </Box>
        }
      />
      {/* 동일 항목 중복 전개 — 순번/총개수 배지 */}
      <Row
        label={t("floLegendDup")}
        swatch={
          <Box
            sx={{
              minWidth: 18,
              height: 14,
              px: "3px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "7px",
              border: `1px solid ${deepOrange[600]}`,
              bgcolor: deepOrange[100],
              color: "#000",
              fontSize: FS.CAPTION,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            1/n
          </Box>
        }
      />
    </Box>
  );
};

export default FloLegend;
